import React, { useCallback, useEffect, useState } from "react";
import { stat, tl } from "@/lib/i18nFormat";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Apple, Plus, AlertTriangle, Trash2, Link2, Pencil } from "lucide-react";
import { enqueue } from "@/pwa/offlineQueue";

import { useI18n } from "@/context/I18nContext";

const STATE_STYLE: Record<string, string> = {
  expired: "bg-red-500 text-white", expires_today: "bg-amber-500 text-white",
  ok: "bg-emerald-600 text-white", resolved: "bg-muted text-muted-foreground", unknown: "bg-muted",
};

export default function Freshness() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("freshness.manage");
  const [tab, setTab] = useState("items");
  return (
    <div data-testid="freshness-page">
      <PageHeader title="Freshness & Use-By" subtitle="Date-labeled items, FEFO rotation and near-expiry alerts (not an inventory count)" testId="freshness-header" />
      <div className="mb-4 flex flex-wrap gap-1.5" data-testid="freshness-tabs">
        {[["items", "Items (FEFO)"], ["alerts", "Alerts"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`freshness-tab-${k}`}
            className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${tab === k ? "bg-brand text-white" : "bg-muted hover:bg-muted/70"}`}>{l}</button>
        ))}
      </div>
      {tab === "items" && <Items canManage={canManage} />}
      {tab === "alerts" && <Alerts />}
    </div>
  );
}

const Items: React.FC<{ canManage: boolean }> = ({ canManage }) => {
  const {
    t
  } = useI18n();

  const [rows, setRows] = useState<any[]>([]);
  const [stores, setStores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => {
    Promise.all([api.get("/freshness/items"), api.get("/org/stores").catch(() => ({ data: { stores: [] } }))])
      .then(([r, s]) => { setRows(r.data.items || []); setStores(s.data.stores || []); })
      .catch(() => setRows([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loading) return <LoadingBlock />;
  return (
    (<div>
      {canManage && (
        <div className="mb-3 flex justify-end">
          <Button size="sm" className="gap-1.5" onClick={() => setCreating(true)} data-testid="fresh-new"><Plus size={15} />{" "}{t("Log item")}</Button>
        </div>
      )}
      {creating && <NewItem stores={stores} onDone={() => { setCreating(false); load(); }} onCancel={() => setCreating(false)} />}
      <Card className="w-full overflow-x-auto p-0" data-testid="fresh-table">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
            <tr><th className="p-2.5">{t("Item")}</th><th className="p-2.5">{t("Category")}</th><th className="p-2.5">{t("Lot")}</th><th className="p-2.5">{t("Use-by")}</th><th className="p-2.5">{t("Days")}</th><th className="p-2.5">{t("Qty note")}</th><th className="p-2.5">{t("Status")}</th>{canManage && <th className="p-2.5">{t("Actions")}</th>}</tr>
          </thead>
          <tbody>
            {rows.length === 0 ? <tr><td colSpan={canManage ? 8 : 7} className="p-6 text-center text-muted-foreground">{t("No items logged")}</td></tr> :
              rows.map((it) => (
                <tr key={it.id} className="border-t border-border" data-testid={`fresh-row-${it.id}`}>
                  <td className="p-2.5 font-medium">{it.name}</td>
                  <td className="p-2.5">{stat(it.category)}</td>
                  <td className="p-2.5 text-muted-foreground">{it.lot || "—"}</td>
                  <td className="p-2.5">{it.use_by_date}</td>
                  <td className="p-2.5"><Badge className={STATE_STYLE[it.freshness_state] || "bg-muted"} data-testid={`fresh-state-${it.id}`}>{it.days_to_use_by ?? "—"}{t("d ·")}{" "}{it.freshness_state}</Badge></td>
                  <td className="p-2.5 text-muted-foreground">{it.quantity_note || "—"}</td>
                  <td className="p-2.5">{stat(it.status)}</td>
                  {canManage && <td className="p-2.5"><ItemActions item={it} onChanged={load} /></td>}
                </tr>
              ))}
          </tbody>
        </table>
      </Card>
    </div>)
  );
};

const ItemActions: React.FC<{ item: any; onChanged: () => void }> = ({ item, onChanged }) => {
  const {
    t
  } = useI18n();

  const dispose = async () => {
    try { await api.post(`/freshness/items/${item.id}/dispose`, { disposition: "disposed", note: "past use-by" }); toast.success("Disposition recorded"); onChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const spawn = async () => {
    try { const r = await api.post(`/freshness/items/${item.id}/action`, {}); toast.success(r.data.created ? tl("Action created") : tl("Action already linked")); onChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (item.status !== "active") return <span className="text-xs text-muted-foreground">{t("resolved")}</span>;
  return (
    (<div className="flex gap-1.5">
      <Button size="sm" variant="outline" className="gap-1" onClick={dispose} data-testid={`fresh-dispose-${item.id}`}><Trash2 size={13} />{" "}{t("Dispose")}</Button>
      <Button size="sm" variant="ghost" className="gap-1" onClick={spawn} data-testid={`fresh-action-${item.id}`}><Link2 size={13} /> {item.linked_action_id ? t("Linked") : t("Action")}</Button>
    </div>)
  );
};

const NewItem: React.FC<{ stores: any[]; onDone: () => void; onCancel: () => void }> = ({ stores, onDone, onCancel }) => {
  const {
    t
  } = useI18n();

  const { user, activeMembership } = useAuth();
  const [f, setF] = useState<any>({ store_id: "", name: "", category: "Deli", lot: "", use_by_date: "", quantity_note: "", location: "" });
  const create = async () => {
    if (!f.store_id || !f.name || !f.use_by_date) { toast.error("Store, name and use-by are required"); return; }
    if (!navigator.onLine) {
      try {
        enqueue("/freshness/items", "POST", f, (activeMembership as any)?.tenant_id || "", user?.id || "");
        window.dispatchEvent(new Event("sc-queue-changed"));
        toast.success("Saved offline — will sync when you reconnect"); onDone();
      } catch (e: any) { toast.error(e?.message || "Cannot queue this offline"); }
      return;
    }
    try {
      const cid = (crypto as any).randomUUID ? (crypto as any).randomUUID() : `cid-${Date.now()}`;
      await api.post("/freshness/items", { ...f, client_id: cid }); toast.success("Item logged"); onDone();
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    (<Card className="mb-3 p-4" data-testid="fresh-new-form">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div><Label className="mb-1 block text-sm">{t("Store")}</Label>
          <select className="h-9 w-full rounded-md border border-border bg-background px-2 text-sm" value={f.store_id} onChange={(e) => setF({ ...f, store_id: e.target.value })} data-testid="fresh-store">
            <option value="">{t("Select store…")}</option>{stores.map((s) => <option key={s.id} value={s.id}>{t("Store")}{" "}{s.store_number}</option>)}
          </select></div>
        <div><Label className="mb-1 block text-sm">{t("Name")}</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="fresh-name" /></div>
        <div><Label className="mb-1 block text-sm">{t("Category")}</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="fresh-category" /></div>
        <div><Label className="mb-1 block text-sm">{t("Lot / batch")}</Label><Input value={f.lot} onChange={(e) => setF({ ...f, lot: e.target.value })} data-testid="fresh-lot" /></div>
        <div><Label className="mb-1 block text-sm">{t("Use-by date")}</Label><Input type="date" value={f.use_by_date} onChange={(e) => setF({ ...f, use_by_date: e.target.value })} data-testid="fresh-useby" /></div>
        <div><Label className="mb-1 block text-sm">{t("Quantity note")}</Label><Input value={f.quantity_note} onChange={(e) => setF({ ...f, quantity_note: e.target.value })} placeholder={t("free text — not a count")} data-testid="fresh-qty" /></div>
      </div>
      <div className="mt-3 flex gap-2"><Button size="sm" onClick={create} data-testid="fresh-create">{t("Save")}</Button><Button size="sm" variant="ghost" onClick={onCancel}>{t("Cancel")}</Button></div>
    </Card>)
  );
};

const Alerts: React.FC = () => {
  const {
    t
  } = useI18n();

  const [data, setData] = useState<any>(null);
  useEffect(() => { api.get("/freshness/alerts", { params: { near_days: 3 } }).then((r) => setData(r.data)).catch(() => setData(null)); }, []);
  if (!data) return <LoadingBlock />;
  const Section = ({ title, items, tone }: any) => (
    <Card className="p-4" data-testid={`fresh-alert-${tone}`}>
      <div className="mb-2 flex items-center gap-2 font-medium"><AlertTriangle size={16} className={tone === "expired" ? t("text-red-500") : t("text-amber-500")} />{title} <Badge variant="secondary">{items.length}</Badge></div>
      {items.length === 0 ? <EmptyState icon={<Apple size={22} />} title="Nothing here" description="No items in this window." /> :
        <ul className="space-y-1.5">{items.map((i: any) => <li key={i.id} className="flex justify-between text-sm" data-testid={`fresh-alert-item-${i.id}`}><span>{i.name} <span className="text-muted-foreground">· {i.category}</span></span><span className="text-muted-foreground">{i.use_by_date} ({i.days_to_use_by}{t("d)")}</span></li>)}</ul>}
    </Card>
  );
  return <div className="grid gap-3 md:grid-cols-2"><Section title={t("Expired")} items={data.expired} tone="expired" /><Section title={t("Near expiry (≤3 days)")} items={data.near_expiry} tone="near" /></div>;
};
